<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

import CheckTaskOutcomeChip from '@/components/CheckTaskOutcomeChip.vue';
import { useCheckTaskPrimaryAction } from '@/composables/useCheckTaskPrimaryAction';
import { relatedObjectToYardAsset } from '@/helpers/checkTaskDetailsTitle';
import YardAssetPresenterV2 from '@satellite/components/elements/presenters/YardAssetPresenterV2.vue';
import { getCheckOutcomeChipDisplay, isTaskInProgress } from '@satellite/helpers/yardTaskUtils';

import type { CheckTaskViewData } from '@nova/core';

const props = defineProps<{
  task: CheckTaskViewData;
}>();

const task = toRef(props, 'task');
const { t } = useI18n();
const { triggerPrimaryAction } = useCheckTaskPrimaryAction();

const relatedObject = computed(() => task.value.relatedObject);
const assetId = computed(() => relatedObject.value?.id ?? null);
const assetYard = computed(() => relatedObjectToYardAsset(relatedObject.value) ?? null);

const showOutcome = computed(() => getCheckOutcomeChipDisplay(task.value) !== null);

const primaryDisabled = computed(
  () =>
    !isTaskInProgress(task.value) ||
    !task.value.id ||
    !task.value.checkId ||
    !task.value.warehouseId ||
    !relatedObject.value?.id
);

function onPrimary() {
  triggerPrimaryAction(task.value);
}
</script>

<template>
  <v-card variant="flat" class="text-center" border data-testid="check-task-details-title">
    <v-card-text class="pt-5">
      <div class="d-flex flex-column align-center w-100 gap-2">
        <template v-if="assetId">
          <yard-asset-presenter-v2
            :asset-id="assetId"
            :placeholder-asset-data="assetYard"
            :display-fields="['code', 'typeChip']"
            :asset-type-chip-props="{ class: 'px-2' }"
            size="large">
            <template #asset-code="{ assetCode }">
              <span class="typography-heading-lg-bold text--color-text-secondary">
                {{ assetCode }}
              </span>
            </template>
            <template #asset-type-chip="{ assetTranslatedType }">
              <span class="text-uppercase typography-chip-sm">{{ assetTranslatedType }}</span>
            </template>
          </yard-asset-presenter-v2>
          <yard-asset-presenter-v2
            :asset-id="assetId"
            :placeholder-asset-data="assetYard"
            :display-fields="['spot']"
            :spot-props="{
              areaProps: { class: 'typography-heading-md text--color-text-secondary' },
              spotCodeProps: { class: 'typography-heading-md-bold text--color-text-secondary' },
              spotIconProps: { color: 'text-secondary', size: 16 }
            }"
            size="regular">
            <template #waiting-assignment>
              <span class="typography-heading-md text--color-text-tertiary">
                {{ t('checkTaskDetails.card.unassignedLocation') }}
              </span>
            </template>
          </yard-asset-presenter-v2>
        </template>
      </div>

      <v-btn
        class="mt-6 mb-2 typography-button-lg"
        size="x-large"
        color="primary"
        :disabled="primaryDisabled"
        append-icon="mdi-arrow-right"
        @click="onPrimary">
        {{ t('checkTaskDetails.buttons.assetDetails') }}
      </v-btn>

      <template v-if="showOutcome">
        <v-divider class="my-4" />
        <div class="d-flex justify-center">
          <check-task-outcome-chip :task="task" />
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>
